import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import FileUpload from '@mui/icons-material/FileUpload';
import MenuItem from '@mui/material/MenuItem';
import { t } from 'i18next';
import { useRef } from 'react';

import { BulkActionsBarButton } from '~components/BulkActionsBar/BulkActionsBar';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { CompanyExportRenderer } from '~hooks/useCompanyExportRenderers/models';
import { getSettlementExportRendererLabel } from '~utils/settlements/settlement-export-utils';

interface SettlementExportButtonProps {
  companyExportRenderers: CompanyExportRenderer[];
  isExporting: boolean;
  onRendererSelect: (companyExportRenderer: CompanyExportRenderer) => void;
}

export default function SettlementExportButton({
  companyExportRenderers,
  isExporting,
  onRendererSelect,
}: SettlementExportButtonProps) {
  const menuHandler = useRef<MenuHandler>(null);

  const [firstRenderer] = companyExportRenderers;

  if (!firstRenderer) {
    return null;
  }

  const hasSingleRenderer = companyExportRenderers.length === 1;

  if (hasSingleRenderer) {
    return (
      <BulkActionsBarButton
        color="inherit"
        data-test-id="settlement-export-button"
        loading={isExporting}
        loadingPosition="start"
        onClick={() => onRendererSelect(firstRenderer)}
        startIcon={<FileUpload />}
      >
        {t('approvals.driver_pay.settlements.settlement_export.export')}
      </BulkActionsBarButton>
    );
  }

  return (
    <Menu
      ref={menuHandler}
      menuTrigger={
        <BulkActionsBarButton
          color="inherit"
          data-test-id="settlement-export-as-button"
          endIcon={<ArrowDropDown />}
          loading={isExporting}
          loadingPosition="start"
          startIcon={<FileUpload />}
        >
          {t('approvals.driver_pay.settlements.settlement_export.export_as')}
        </BulkActionsBarButton>
      }
    >
      {companyExportRenderers.map((companyExportRenderer) => (
        <MenuItem
          data-test-id={`settlement-export-renderer-${companyExportRenderer.rendererKey}`}
          key={companyExportRenderer.id}
          onClick={() => {
            menuHandler.current?.onClose?.();
            onRendererSelect(companyExportRenderer);
          }}
        >
          {getSettlementExportRendererLabel(companyExportRenderer.rendererKey)}
        </MenuItem>
      ))}
    </Menu>
  );
}
